import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil.svg';
import { ReactComponent as RevenueManagementIcon } from '../../../assets/icons/sidebar/revenue-sharing-icon.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import {
  FilterRevenueInterFace,
  ManagementStationInterFace,
} from '../../../interface/revenue-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import {
  fetchAllStations,
  updateSingleStation,
  setRevenueStationFilterData,
  updateRevenueManagementStationFilters,
  fetchRevenueManagementStationFilters,
  disableRevenueManagementStationFilterApplied,
  setSortData,
} from '../../../redux/slices/RevenueManagementStationSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import revenueApi from '../../../utils/api/revenue';

import FilterRevenue from './filter-station-management';
import RevenueManagementToggle from './RevenueManageToggle';

interface ModalState {
  status: boolean;
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
}

interface ManagementStationRevenueProperties {
  currentPage: string;
  selectedAccountId: string | number;
  setCurrentPage: React.Dispatch<React.SetStateAction<string>>;
}

interface EditPercentageProperties {
  id: string;
  value: number;
}

/**
 * ManagementStationRevenue page function component
 */
function ManagementStationRevenue(properties: ManagementStationRevenueProperties) {
  const { t, i18n } = useTranslation('revenue');
  const { currentPage, setCurrentPage, selectedAccountId } = properties;

  const dispatch = useAppDispatch();
  const inputReference = useRef<HTMLInputElement>(null);
  const {
    stations,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filter,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.revenueStation);

  const [editPercent, setEditPercent] = useState<EditPercentageProperties>({
    id: '',
    value: 0,
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);

  /**
   * Function to handle edit percentage
   */
  const handleEditPercentage = (rowData: ManagementStationInterFace) => {
    setEditPercent({
      id: rowData.id,
      value: rowData.revenuePercentage ? rowData.revenuePercentage : 0,
    });
  };

  /**
   * API call to update percentage
   */
  const handleUpdateRevenuePercentage = async () => {
    await revenueApi
      .editAccountPercentage({
        revenuePercentage: editPercent.value.toString(),
        stationId: editPercent.id,
      })
      .then(() => {
        const updatedAccount: ManagementStationInterFace | undefined = stations.find(
          (station) => station.id === editPercent.id,
        );
        if (updatedAccount) {
          dispatch(
            updateSingleStation({
              ...updatedAccount,
              revenuePercentage: editPercent.value,
            }),
          );
        }
        setEditPercent({
          id: '',
          value: 0,
        });
      })
      .catch((error: any) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Function to handle click outside of input field
   * @param event
   */
  const handleOutsideClick = async (event: MouseEvent) => {
    if (editPercent.id !== '') {
      handleUpdateRevenuePercentage();
    }

    if (inputReference.current && !inputReference.current.contains(event.target as Node)) {
      setEditPercent({
        id: '',
        value: 0,
      });
    }
  };

  /**
   * Function to handle percentage edit
   * @param name
   * @param value
   */
  const handleChangePercentage = (value: string) => {
    const percentage = Math.min(100, Math.max(0, Math.floor(Number(value) || 0)));
    setEditPercent({
      ...editPercent,
      value: percentage,
    });
  };

  const column = [
    {
      label: t('stationName'),
      accessorKey: 'name' as keyof ManagementStationInterFace,
      sortAllow: true,
    },
    {
      label: t('address'),
      accessorKey: 'address' as keyof ManagementStationInterFace,
      sortAllow: true,
    },
    {
      label: t('percentage'),
      accessorKey: 'revenuePercentage' as keyof ManagementStationInterFace,
      sortAllow: true,
      /**
       * Function to handle input for edit percentage
       * @param rowData
       */
      customData: (rowData: ManagementStationInterFace) => (
        <div className="table-row-data">
          {editPercent.id === rowData.id ? (
            <span>
              <input
                ref={inputReference}
                type="number"
                value={editPercent.value || ''}
                placeholder="0"
                onChange={(event) => handleChangePercentage(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleUpdateRevenuePercentage();
                  }
                }}
                className="table-row-input-field"
              />
            </span>
          ) : (
            <div className="value-container">
              <p className="value">{rowData.revenuePercentage}</p>
              <span>%</span>
            </div>
          )}
        </div>
      ),
      columnWidth: 40,
    },
    {
      label: t('action'),
      accessorKey: '' as keyof ManagementStationInterFace,
      sortAllow: false,
      /**
       * Function for action buttons
       * @param rowData
       */
      customData: (rowData: ManagementStationInterFace) => (
        <div className="action-button-group">
          <div
            className="action-button-item view-color"
            onClick={() => handleEditPercentage(rowData)}
            role="presentation">
            <EditIcon />
          </div>
        </div>
      ),
      columnWidth: 60,
    },
  ];

  const [modalView, setModalView] = useState<ModalState>({
    status: false,
    title: '',
    header: true,
    content: <div />,
    rightCorner: '',
  });

  /**
   * Fetch Users list function
   */
  const fetchStationsData = (data: any) => {
    if (!loading) {
      dispatch(fetchAllStations(data));
    }
  };
  /**
   * Handle submit filter function
   * @param value
   */
  const handleSubmitFilter = (filterData: FilterRevenueInterFace) => {
    dispatch(setRevenueStationFilterData(filterData));
    fetchStationsData({
      ...filterData,
      page: DefaultApiParameters.page,
      limit,
    });
    setModalView((previous) => ({
      ...previous,
      status: false,
    }));
  };

  /**
   * Filter clear
   */
  const clearFilter = () => {
    dispatch(setRevenueStationFilterData({}));
    fetchStationsData({
      page: DefaultApiParameters.page,
      limit,
    });
  };

  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: true,
      content: <div />,
      rightCorner: '',
    });
  };

  /**
   * User filter function
   */
  const filterFunction = () => {
    setModalView({
      status: true,
      title: t('rolesFilter'),
      header: false,
      content: (
        <FilterRevenue
          closeModal={closeModal}
          handleSubmitFilter={handleSubmitFilter}
          oldValue={filter}
        />
      ),
      rightCorner: '',
    });
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchStationsData({ ...queryParameters, ...value, sortField, sortBy });
  };
  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    const request: any = { submissionStatus: 'approved', ...queryParameters };
    if (selectedAccountId) {
      request.selectedAccountId = selectedAccountId;
    }
    if (sortBy) {
      request.sortBy = sortBy;
      request.sortField = sortField;
    }
    revenueApi
      .fetchChargingStationsExport(request)
      .then((response) => {
        // Convert data to CSV format
        const headers = [t('sNo'), t('stationName'), t('address'), t('percentage')];
        const rows = response.data.map((row: ManagementStationInterFace, index: number) => [
          index + 1,
          row.name,
          row.address,
          `${row.revenuePercentage ? row.revenuePercentage : 0}%`,
        ]);
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'revenue-management-station-list.csv');
          } else {
            exportPDF(headers, rows, 'revenue-management-station-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchStationsData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchStationsData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [editPercent]);

  useEffect(() => {
    if (isFilterUpdated || stations.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchStationsData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableRevenueManagementStationFilterApplied());
    }
  }, [filters]);

  useEffect(() => {
    if (editPercent.id && inputReference.current) {
      inputReference.current.focus(); // Ensure the input is focused after state update
    }
  }, [editPercent.id]);
  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('revenueManagement'),
        icon: <RevenueManagementIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults]);

  return (
    <div className="table-content" dir={i18n.dir()}>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
      />
      <CustomTable
        header={column}
        data={stations}
        serialNo
        height={window.innerHeight >= 700 ? 327 : 288}
        version="v2"
        loading={loading}
        page={page}
        limit={limit}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        filters={filters}
        filtersToDispatch={updateRevenueManagementStationFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchRevenueManagementStationFilters}
        filterClearFunction={clearFilter}
        filterFunction={filterFunction}
        sizeChangeButtonRequired
        rightTopContent={
          <RevenueManagementToggle currentPage={currentPage} setCurrentPage={setCurrentPage} />
        }
        tableHeaderVersion="v1"
        refreshRequired
        handleRefresh={handleRefresh}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        ReferenceCurrentPages={handleRefresh}
        hoverLabel={t('refresh')}
      />
    </div>
  );
}

export default ManagementStationRevenue;
